// Shared runner type + display formatters used by RaceDetail and RunnerRow.
// Kept in one place so the runner list and its expandable detail render
// numbers/odds identically.

export interface Runner {
  _id: string;
  horse_number: number;
  horse_name: string;
  jockey: string;
  trainer: string;
  barrier: number;
  weight: string;
  fixed_odd_win: number;
  fixed_odd_place: number;
  tote_win: number;
  tote_place: number;
  scratched: number;
  tips: number[];
  dividends?: any[];
  // PuntingForm enrichment
  sp_price?: number;
  pf_form?: string;
  pf_win_pct?: number | null;
  pf_place_pct?: number | null;
  pf_age?: number | null;
  pf_sex?: string | null;
  pf_rating?: number | null;
  pf_last_runs?: any[];
  pf_days_last_run?: number | null;
  pf_career?: { starts: number; wins: number; seconds: number; thirds: number } | null;
  // Latest recorded Core model run for this race.
  core_rank?: number | null;
  core_rated_price?: number | null;
  core_market_price?: number | null;
  core_market_price_source?: string | null;
  // Betfair enrichment
  bf_selection_id?: number;
  bf_back?: number | null;
  bf_back_size?: number | null;
  bf_lay?: number | null;
  bf_lay_size?: number | null;
  bf_last_traded?: number | null;
  bf_bsp?: number | null;
  bf_prices_back?: { price: number; size: number }[];
  bf_prices_lay?: { price: number; size: number }[];
  bf_status?: string;
  // Recorded WIN back-price snapshots for the race screen (open / 10am / last 30m).
  bf_open?: number | null;
  bf_ten_am?: number | null;
  bf_last30?: number | null;
}

export const SEX_LABELS: { [key: string]: string } = {
  G: 'Gelding', M: 'Mare', H: 'Horse', F: 'Filly', C: 'Colt', R: 'Rig',
};

export const formatOdds = (odds: number | undefined | null) => {
  if (odds == null || odds === 0) return '—';
  return odds.toFixed(2);
};

export const formatBFOdds = (val: number | null | undefined) => {
  if (val == null || val === 0) return '—';
  return val.toFixed(2);
};

export const formatBFSize = (val: number | null | undefined) => {
  if (val == null) return '—';
  if (val >= 1000) return `$${(val / 1000).toFixed(1)}k`;
  return `$${val.toFixed(0)}`;
};

export const formatCareer = (career?: Runner['pf_career']) => {
  if (!career || !career.starts) return '—';
  return `${career.starts}: ${career.wins}-${career.seconds}-${career.thirds}`;
};

export const formatPct = (value?: number | null) =>
  value == null ? '—' : `${Math.round(value)}%`;

export const formatWeight = (weight: string | undefined) => {
  if (!weight) return '—';
  return /kg$/i.test(weight) ? weight : `${weight}kg`;
};

export const asText = (value: unknown, fallback = 'N/A') => {
  if (value == null) return fallback;
  // Treat whitespace-only strings (e.g. PuntingForm's undeclared rider " ") as empty.
  if (typeof value === 'string') return value.trim() || fallback;
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (typeof value === 'object') {
    const record = value as Record<string, unknown>;
    const candidate =
      (typeof record.fullName === 'string' && record.fullName) ||
      (typeof record.name === 'string' && record.name) ||
      (typeof record.trainerName === 'string' && record.trainerName) ||
      (typeof record.jockeyName === 'string' && record.jockeyName) ||
      (typeof record.driverName === 'string' && record.driverName) ||
      '';
    return (typeof candidate === 'string' && candidate.trim()) || fallback;
  }
  return fallback;
};
